import { useMutation } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { preparePatchGroup, preparePostGroup } from './groups.prepare';
import type { GroupType, PatchGroup, PostGroup, SmallestGroup } from './types';

export type BaseGroup = API_JSON & {
  name: string;
  description?: string;
  year: string;
  order: number;
  customer: API_JSON;
  type?: GroupType;
  externalId?: number;
};

const order = { 'order[year]': 'desc', 'order[name]': 'asc' } as const;
const groups = ['group-details'];
const include = '';

/**
 * Fetch groups (singular)
 * @param params can be used to customise/limit the groups retrieved
 * @returns a single groups
 */
export const getGroup: GetOne<BaseGroup> = async (id, params) =>
  (
    await ASSIST_API.get(`groups/${id}`, {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

export const getGroupWithNoDefaults: GetOne<SmallestGroup> = async (id, params) =>
  (await ASSIST_API.get(`groups/${id}`, { params: requestParams({}, params) })).data;

/**
 * Fetch groups
 * @param params can be used to customise/limit the  groups retrieved
 * @returns the requested groups
 */
export const getGroups: GetMany<BaseGroup> = async (params) =>
  (
    await ASSIST_API.get('groups', {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

export const getGroupsWithNoDefaults: GetMany<SmallestGroup> = async (params) =>
  (await ASSIST_API.get('groups', { params: requestParams({}, params) })).data;

/**
 * Given a group id fetch and return the area
 *  - only runs once group id is provided
 *  - fetches the one group
 *  - caches data
 * @param id group id
 * @param params optional parameters to change requested data
 * @returns the matching group
 */
export const useGroup: AssistQuery.UseOne<BaseGroup> = (id, params, options) =>
  useQueryAssist(['groups', { id, ...params }], () => getGroup(id, params), {
    ...options,
    enabled: !!id
  });

export const useGroupWithNoDefaults: AssistQuery.UseOne<SmallestGroup> = (id, params, options) =>
  useQueryAssist(
    ['groups', { id, ...params }],
    () => (id ? getGroupWithNoDefaults(id, params) : undefined),
    {
      ...options,
      enabled: !!id
    }
  );

/**
 * Hook implementation to fetch groups
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns groups matching request
 */
export const useGroups: AssistQuery.UseMany<BaseGroup> = (params, options) =>
  useQueryAssist(['groups', params], () => getGroups(params), options);

export const useGroupsWithNoDefaults: AssistQuery.UseMany<SmallestGroup> = (params, options) =>
  useQueryAssist(['groups', params], () => getGroupsWithNoDefaults(params), options);

/**
 * Creates methods to send a DELETE request to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteGroup: AssistQuery.UseMutation<BaseGroup, number> = (options) =>
  useMutation((id) => ASSIST_API.delete(`groups/${id}`), {
    ...options,
    onSettled: (...args) => {
      AssistQueryClient.invalidateQueries(['groups']);
      if (options?.onSettled) {
        options.onSettled(...args);
      }
    }
  });

/**
 * Creates methods to send DELETE requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteGroups: AssistQuery.UseMutations<undefined, number[]> = (options) =>
  useMutation(
    (ids: number[]) => Promise.allSettled(ids.map((id) => ASSIST_API.delete(`groups/${id}`))),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['groups']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

export const usePostGroup: AssistQuery.UseMutation<PostGroup, PostGroup> = (options) =>
  useMutation(
    (postGroup: PostGroup) =>
      ASSIST_API.post('groups', preparePostGroup(postGroup), {
        params: requestParams({ groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['groups']);
        if (options?.onSettled) {
          options?.onSettled(...args);
        }
      }
    }
  );

export const usePatchGroup: AssistQuery.UseMutation<BaseGroup, PatchGroup> = (options) =>
  useMutation(
    ({ id, data }: PatchGroup) =>
      ASSIST_API.patch(`groups/${id}`, preparePatchGroup(data), {
        params: requestParams({ groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['groups']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );
